import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  fileUpload,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../../config";
import { debounce } from "lodash";
import MainBody from "../../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../../components/Pagination";
import sortCaret from "../../../../components/SortCaret";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import { CheckCircle2Icon, LucidePlusCircle, XCircle } from "lucide-react";
import AddEditQuestion from "./AddEditQuestion";
import QuestionActionButton from "./QuestionActionButton";
import Confirmation from "../../../../components/Confirmation";
import AdminFilters from "../../../../components/AdminFilters";
import { Button } from "../../../../components/ui/button";

const Questions = ({ expandList = false, questionData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [questionList, setQuestionsList] = useState(
    expandList ? questionData : []
  );

  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [questionId, setQuestionId] = useState(null);
  const [showAddEditQuestion, setShowAddEditQuestion] = useState(false);
  const [showQuestionDelete, setShowQuestionDelete] = useState(false);

  const [versionId, setVersionId] = useState();
  const [sectionType, setSectionType] = useState();
  const [statusId, setStatusId] = useState();
  const [organizationTypeId, setOrganizationTypeId] = useState();
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [showActive, setShowActive] = useState(false);

  const toggleShowActive = () => {
    setShowActive(!showActive);
  };

  const [showDeactive, setShowDeactive] = useState(false);

  const toggleShowDeactive = () => {
    setShowDeactive(!showDeactive);
  };
  const toggleShowDelete = () => {
    if (showQuestionDelete) {
      setQuestionId(null);
    }
    setShowQuestionDelete(!showQuestionDelete);
  };

  const toggleShowAddEditQuestion = () => {
    if (showAddEditQuestion) {
      setEdit(false);
      setEditItem(null);
      setQuestionId(null);
    }
    setShowAddEditQuestion(!showAddEditQuestion);
  };

  const fetchQuestions = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_QUESTIONS}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&organization_type_id=${
            organizationTypeId ? organizationTypeId : ""
          }&version_id=${versionId ? versionId : ""}&status=${
            statusId ? statusId : ""
          }&section_type=${sectionType ? sectionType : ""}`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const questionList = data.data.data;
        setQuestionsList(questionList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setQuestionsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortField, sortOrder, versionId, organizationTypeId, statusId, sectionType]
  );

  const fetchQuestionActive = useCallback(
    async (selectedRowIds, status, id) => {
      try {
        setLoading(true);
        const formData = new FormData();

        if (selectedRowIds?.length > 0) {
          for (let i = 0; i < selectedRowIds?.length; i++) {
            formData.append(`ids[${i}]`, selectedRowIds[i]);
          }
        }

        if (id) {
          formData.append(`ids[0]`, id);
        }

        formData.append(`status`, status);

        formData.append("_method", "put");

        const { data } = await fileUpload(
          `${API.GET_ADMIN_QUESTIONS_STATUS}`,
          formData
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const questionList = data.data.data;
        setQuestionsList(questionList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setQuestionsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortField, sortOrder, versionId, organizationTypeId]
  );

  const questionsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: questionList[i]?.id,
        id: questionList[i]?.id,
        organization_type: questionList[i]?.organization_type?.name
          ? questionList[i]?.organization_type?.name
          : "",
        version_number: questionList[i]?.version?.version_number
          ? `${questionList[i]?.version?.version_number} - ${questionList[i]?.version?.name}`
          : "",
        order: questionList[i]?.order ? questionList[i]?.order : "",
        label: questionList[i]?.label ? questionList[i]?.label : "",
        question: questionList[i]?.question ? questionList[i]?.question : "-",
        section_type: questionList[i]?.section?.section_type
          ? questionList[i]?.section?.section_type
          : "",
        section_name: questionList[i]?.section?.title
          ? questionList[i]?.section?.title
          : "",
        is_used: questionList[i]?.is_used,
        status: (
          <div className="flex items-center justify-center w-full">
            {questionList[i]?.status ? (
              <CheckCircle2Icon size={18} className="text-green-600" />
            ) : (
              <XCircle size={18} className="text-red-500" />
            )}
          </div>
        ),
        action: (
          <>
            <QuestionActionButton
              id={questionList[i]?.id}
              setEdit={setEdit}
              setQuestionId={setQuestionId}
              toggleShowAddEditQuestion={toggleShowAddEditQuestion}
              toggleShowDelete={toggleShowDelete}
              is_used={questionList[i]?.is_used}
            />
          </>
        ),
      });
    }
    return items;
  };

  const questions = questionsGenerator(questionList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "organization_type",
      text: "Industry Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "version_number",
      text: "Version Number",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "order",
      text: "Order",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "label",
      text: "Label",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "question",
      text: "Question",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "section_type",
      text: "Section Type",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "section_name",
      text: "Section",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "status",
      text: "Status",
      align: "center",
      headerAlign: "center",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const arrayRemove = (arr, value) => {
    return arr.filter(function (ele) {
      return ele !== value;
    });
  };

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    onSelectAll: (row, isSelect) => {
      if (questionList?.length > 0) {
        if (row) {
          const ids =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect?.map((item) => {
              return item?.key;
            });
          setSelectedRowIds(selectedRowIds ? selectedRowIds.concat(ids) : ids);
        } else {
          const removableData =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect.map(function (data) {
              return data?.key;
            });
          setSelectedRowIds(
            selectedRowIds
              ? selectedRowIds.filter((item) => !removableData.includes(item))
              : []
          );
        }
      }
    },
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedRowIds((keys) => [...keys, row?.key]);
      } else {
        setSelectedRowIds(arrayRemove(selectedRowIds, row?.key));
      }
    },
  };

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    if (!expandList) {
      navigate(
        `?tab=risk-assessment&childTab=questions&limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }&search=${searchParam ? searchParam : ""}&sort_column=${
          sortField ? sortField : ""
        }`,
        {
          replace: true,
        }
      );
    }
  }, [navigate, limit, searchParam, page, sortField, sortOrder, expandList]);

  useEffect(() => {
    if (!expandList) {
      fetchQuestions();
    }
  }, [expandList, fetchQuestions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchQuestions(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchQuestions(limit, page);
  };

  // Single Question Delete
  const handleQuestionDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.ADMIN_QUESTION_DELETE}/${questionId}`);
      setLoading(false);
      fetchQuestions();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        search={!expandList}
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          !expandList && (
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => toggleShowActive()}
                disabled={selectedRowIds?.length === 0}
                className="bg-green-600"
              >
                <div className="flex items-center gap-1">
                  <CheckCircle2Icon size={18} />
                  <span>Active</span>
                </div>
              </Button>
              <Button
                onClick={() => toggleShowDeactive()}
                disabled={selectedRowIds?.length === 0}
                className="bg-red-500"
              >
                <div className="flex items-center gap-1">
                  <XCircle size={18} />
                  <span>Deactive</span>
                </div>
              </Button>
              <AdminFilters
                organizationType={true}
                setOrganizationTypeId={setOrganizationTypeId}
                organizationTypeId={organizationTypeId}
                version={true}
                setVersionId={setVersionId}
                versionId={versionId}
                status={true}
                statusId={statusId}
                setStatusId={setStatusId}
                sectionTypeSelect={true}
                setSectionType={setSectionType}
                sectionType={sectionType}
              />
              {/* Add Question */}
              <ResponsiveDrawerForm
                description={""}
                title={edit ? "Edit Question(s)" : "Add Question(s)"}
                handleOpen={() => {}}
                handleClose={() => {
                  setEdit(false);
                  setQuestionId();
                  setEditItem(null);
                }}
                buttonText={
                  <div className="flex items-center gap-1">
                    <LucidePlusCircle size={18} />
                    <span>Add Question</span>
                  </div>
                }
                open={showAddEditQuestion}
                buttonClassName={""}
                setOpen={setShowAddEditQuestion}
                mobileCancelHidden={true}
                className={"sm:max-w-3xl"}
              >
                <AddEditQuestion
                  edit={edit}
                  editItem={editItem}
                  toggleShowAddEditQuestion={toggleShowAddEditQuestion}
                  id={questionId}
                  fetchQuestionData={() => fetchQuestions(limit, page)}
                />
              </ResponsiveDrawerForm>
            </div>
          )
        }
        loading={loading}
        innerBody={true}
        expandList={true}
        className="p-0"
        mainClassName="w-[calc(100%-3rem)]"
      >
        <div className="custom-table">
          <div className="table-body-class max-h-[550px]">
            {expandList ? (
              <BootstrapTable
                keyField="key"
                remote
                data={questions}
                columns={columns}
                onTableChange={handleTableChange}
                rowClasses={TRClasses}
                noDataIndication="No Data Found"
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            ) : (
              <BootstrapTable
                keyField="key"
                remote
                data={questions}
                selectRow={selectRow}
                columns={columns}
                onTableChange={handleTableChange}
                rowClasses={TRClasses}
                noDataIndication="No Data Found"
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            )}
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
          <Confirmation
            title={"Delete Question"}
            open={showQuestionDelete}
            setOpen={setShowQuestionDelete}
            toggleShow={() => {
              toggleShowDelete();
            }}
            className="sm:max-w-1/2"
            message={"Are you sure you want to delete question?"}
            okAction={async () => {
              handleQuestionDelete();
              toggleShowDelete();
              fetchQuestions();
            }}
            cancelAction={() => {
              setQuestionId(null);
              setShowQuestionDelete(!showQuestionDelete);
            }}
          />

          <Confirmation
            title={"Active Question"}
            open={showActive}
            setOpen={setShowActive}
            toggleShow={() => {
              toggleShowActive();
            }}
            className="sm:max-w-1/2"
            message={"Are you sure you want to active the selected question?"}
            okAction={async () => {
              await fetchQuestionActive(selectedRowIds, 1);
              toggleShowActive();
              fetchQuestions();
            }}
            cancelAction={() => {
              setShowActive(!showActive);
            }}
          />

          <Confirmation
            title={"Deactive Question"}
            open={showDeactive}
            setOpen={setShowDeactive}
            toggleShow={() => {
              toggleShowDeactive();
            }}
            className="sm:max-w-1/2"
            message={"Are you sure you want to deactive the selected question?"}
            okAction={async () => {
              await fetchQuestionActive(selectedRowIds, 0);
              toggleShowDeactive();
              fetchQuestions();
            }}
            cancelAction={() => {
              setShowDeactive(!showDeactive);
            }}
          />
        </div>
      </MainBody>
    </>
  );
};

export default Questions;
